import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  AppBar,
  Typography,
  CssBaseline,
  Button,
  LinearProgress,
  Collapse,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  People as PeopleIcon,
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
  ReceiptLong as ReceiptLongIcon,
  Description as DescriptionIcon,
  Notifications as NotificationsIcon,
  EmojiEvents as EmojiEventsIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Menu as MenuIcon,
  ContactSupport as ContactSupportIcon,
  ReportProblem as ReportProblemIcon,
} from "@mui/icons-material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TopicIcon from '@mui/icons-material/Topic';
import QuizIcon from '@mui/icons-material/Quiz';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import StorageIcon from '@mui/icons-material/Storage';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import StyleIcon from '@mui/icons-material/Style';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import FlagIcon from '@mui/icons-material/Flag';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import InsightsIcon from '@mui/icons-material/Insights';
import { Logout, AccountCircle, Science as ScienceIcon, Settings as SettingsIcon } from "@mui/icons-material";
import CapitalizeFirstLetter from "../../CapitalizeFirstLetter";
import api from "../../api";
import { getAdminPermissionsFromStorage } from "../../utils/adminPermissions";
import { getTesterPermissionsFromStorage } from "../../utils/testerPermissions";
import { masterFormRoutePermissions } from "../../constants/masterFormPermissions";
import TesterPortalSwitcher from "../../components/TesterPortalSwitcher";

const drawerWidth = 240;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [openMenus, setOpenMenus] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactAppBar = useMediaQuery(theme.breakpoints.down("sm"));
  const appBarHeight = isCompactAppBar ? 56 : 70;
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const [announcementCount, setAnnouncementCount] = useState(0);
  const isTesterRole = localStorage.getItem("role") === "tester";
  const [adminPermissions, setAdminPermissions] = useState(() =>
    isTesterRole
      ? (getTesterPermissionsFromStorage() ?? [])
      : (getAdminPermissionsFromStorage() ?? [])
  );

  // Sync from API so sidebar matches Spatie even if localStorage is missing/stale (e.g. session before this feature).
  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_ADMIN_PERMISSIONS_SYNC === "true") {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Treat 404 as non-fatal: older / not-yet-deployed APIs omit GET api/admin/auth/permissions.
        const res = await api.get("admin/auth/permissions", {
          validateStatus: (status) => status === 200 || status === 404,
        });
        if (cancelled) {
          return;
        }
        if (res.status === 404) {
          return;
        }
        const data = res.data;
        if (!data?.success || !Array.isArray(data.data?.permissions)) {
          return;
        }
        const perms = data.data.permissions;
        const storageKey = isTesterRole ? "tester-permissions" : "admin-permissions";
        localStorage.setItem(storageKey, JSON.stringify(perms));
        setAdminPermissions(perms);
      } catch {
        // Keep cached permissions from login / localStorage
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTesterRole]);

  const canViewParents = adminPermissions.includes("view-parents");
  const canViewParentsSubscriptions = adminPermissions.includes(
    "view-parents-subscriptions"
  );
  const canViewMobileUsers =
    adminPermissions.includes("view-mobile-users") || canViewParentsSubscriptions;
  const canViewStudents = adminPermissions.includes("view-students");
  const canCreateStudents = adminPermissions.includes("create-students");
  const canViewTutors = adminPermissions.includes("view-tutors");
  const canUpdateTutors = adminPermissions.includes("update-tutors");
  const canViewCertificates = adminPermissions.includes("view-certificates");
  const canViewAwards = adminPermissions.includes("view-awards");
  const canViewAnnouncements = adminPermissions.includes("view-announcements");
  const canAccessMockExamCategories =
    adminPermissions.includes("view-mock-exam-categories") ||
    adminPermissions.includes("create-mock-exam-categories") ||
    adminPermissions.includes("update-mock-exam-categories") ||
    adminPermissions.includes("delete-mock-exam-categories");
  const canViewMockExams = adminPermissions.includes("view-mock-exams");
  const canCreateMockExams = adminPermissions.includes("create-mock-exams");
  const canUpdateMockExams = adminPermissions.includes("update-mock-exams");
  const canDeleteMockExams = adminPermissions.includes("delete-mock-exams");
  const canAccessMockExamsMenu =
    canViewMockExams ||
    canCreateMockExams ||
    canUpdateMockExams ||
    canDeleteMockExams;
  const canAccessPaperCategories =
    adminPermissions.includes("view-paper-categories") ||
    adminPermissions.includes("create-paper-categories") ||
    adminPermissions.includes("update-paper-categories") ||
    adminPermissions.includes("delete-paper-categories");
  const canViewFlaggedQuestions = adminPermissions.includes(
    "view-flagged-questions"
  );
  const canViewVerificationDashboard = adminPermissions.includes("view-verification-dashboard");
  const canManageExamModes = adminPermissions.includes("manage-exam-modes");
  const canViewLeads = adminPermissions.includes("view-leads");
  const canSendWhatsApp = adminPermissions.includes("send-whatsapp-msg");
  const canViewSupportChat = adminPermissions.includes("view-support-chat");
  const canViewAuditTrail = adminPermissions.includes("view-audit-trail");
  const canAccessLeadsSection = canViewLeads || canSendWhatsApp || canViewSupportChat;
  const canViewMobileHelpSupport = canViewLeads || canViewSupportChat;
  const canViewFlashCards = adminPermissions.includes("view-flash-cards");
  const canCreateFlashCards = adminPermissions.includes("create-flash-cards");

  const canViewPapers = adminPermissions.includes("view-papers");
  const canCreatePapers = adminPermissions.includes("create-papers");
  const canUpdatePapers = adminPermissions.includes("update-papers");
  const canDeletePapers = adminPermissions.includes("delete-papers");
  const canAccessPaperExtract =
    adminPermissions.includes("view-paper-extracts") ||
    adminPermissions.includes("create-paper-extracts") ||
    adminPermissions.includes("update-paper-extracts") ||
    adminPermissions.includes("delete-paper-extracts");
  const canViewPaperPurchases = adminPermissions.includes(
    "view-paper-purchases"
  );
  const canAccessPapersMenu =
    canViewPapers ||
    canCreatePapers ||
    canUpdatePapers ||
    canDeletePapers ||
    canAccessPaperExtract ||
    canViewPaperPurchases;

  const canViewAssignments = adminPermissions.includes("view-assignments");
  const canCreateAssignments = adminPermissions.includes("create-assignments");
  const canUpdateAssignments = adminPermissions.includes("update-assignments");
  const canDeleteAssignments = adminPermissions.includes("delete-assignments");
  const canUseAiAssignmentCreator = adminPermissions.includes(
    "use-ai-assignment-creator"
  );
  const canAccessAssignmentsMenu =
    canViewAssignments ||
    canCreateAssignments ||
    canUpdateAssignments ||
    canDeleteAssignments ||
    canUseAiAssignmentCreator;

  const canViewQuestionBank = adminPermissions.includes("view-question-bank");
  const canCreateQuestionBank = adminPermissions.includes("create-question-bank");
  const canUpdateQuestionBank = adminPermissions.includes("update-question-bank");
  const canViewQuestionBankImports = adminPermissions.includes(
    "view-question-bank-imports"
  );
  const canCreateQuestionBankImports = adminPermissions.includes(
    "create-question-bank-imports"
  );
  const canUpdateQuestionBankImports = adminPermissions.includes(
    "update-question-bank-imports"
  );
  const canViewQuestionBankPapers = adminPermissions.includes(
    "view-question-bank-papers"
  );
  const canViewQuestionBankSimilarPapers = adminPermissions.includes(
    "view-question-bank-similar-papers"
  );
  const canUpdateQuestionBankSimilarPapers = adminPermissions.includes(
    "update-question-bank-similar-papers"
  );
  const canAccessQuestionBankMenu =
    canViewQuestionBank ||
    canCreateQuestionBank ||
    canUpdateQuestionBank ||
    canViewQuestionBankImports ||
    canCreateQuestionBankImports ||
    canUpdateQuestionBankImports ||
    canViewQuestionBankPapers ||
    canViewQuestionBankSimilarPapers ||
    canUpdateQuestionBankSimilarPapers;

  const canViewGameTopics = adminPermissions.includes("view-game-topics");
  const canCreateGameTopics = adminPermissions.includes("create-game-topics");
  const canUpdateGameTopics = adminPermissions.includes("update-game-topics");
  const canDeleteGameTopics = adminPermissions.includes("delete-game-topics");
  const canViewGameContents = adminPermissions.includes("view-game-contents");
  const canCreateGameContents = adminPermissions.includes("create-game-contents");
  const canUpdateGameContents = adminPermissions.includes("update-game-contents");
  const canDeleteGameContents = adminPermissions.includes("delete-game-contents");
  const canAccessGameTopicsSection =
    canViewGameTopics ||
    canCreateGameTopics ||
    canUpdateGameTopics ||
    canDeleteGameTopics;
  const canAccessGameContentsSection =
    canViewGameContents ||
    canCreateGameContents ||
    canUpdateGameContents ||
    canDeleteGameContents;
  const canAccessGamesMenu =
    canAccessGameTopicsSection || canAccessGameContentsSection;

  const canViewCourses = adminPermissions.includes("view-courses");
  const canCreateCourses = adminPermissions.includes("create-courses");
  const canUpdateCourses = adminPermissions.includes("update-courses");
  const canDeleteCourses = adminPermissions.includes("delete-courses");
  const canViewCourseTopics = adminPermissions.includes("view-course-topics");
  const canCreateCourseTopics = adminPermissions.includes("create-course-topics");
  const canUpdateCourseTopics = adminPermissions.includes("update-course-topics");
  const canDeleteCourseTopics = adminPermissions.includes("delete-course-topics");
  const canViewCourseDurations = adminPermissions.includes("view-course-durations");
  const canCreateCourseDurations = adminPermissions.includes("create-course-durations");
  const canUpdateCourseDurations = adminPermissions.includes("update-course-durations");
  const canDeleteCourseDurations = adminPermissions.includes("delete-course-durations");
  const canViewCourseTimeslots = adminPermissions.includes("view-course-timeslots");
  const canCreateCourseTimeslots = adminPermissions.includes("create-course-timeslots");
  const canUpdateCourseTimeslots = adminPermissions.includes("update-course-timeslots");
  const canDeleteCourseTimeslots = adminPermissions.includes("delete-course-timeslots");
  const canViewCourseReports = adminPermissions.includes("view-course-reports");
  const canViewAnalytics = adminPermissions.includes("view-analytics");
  const canAccessMasterForms = masterFormRoutePermissions().some((p) =>
    adminPermissions.includes(p)
  );

  const canAccessCourseContentSection =
    canViewCourseTopics ||
    canCreateCourseTopics ||
    canUpdateCourseTopics ||
    canDeleteCourseTopics;
  const canAccessCourseDurationSection =
    canViewCourseDurations ||
    canCreateCourseDurations ||
    canUpdateCourseDurations ||
    canDeleteCourseDurations ||
    canViewAssignments ||
    canCreateAssignments ||
    canUpdateAssignments ||
    canDeleteAssignments;
  const canAccessCourseTimeslotsSection =
    canViewCourseTimeslots ||
    canCreateCourseTimeslots ||
    canUpdateCourseTimeslots ||
    canDeleteCourseTimeslots;
  const canAccessCourseConfigurationsSection =
    canAccessCourseContentSection ||
    canAccessCourseDurationSection ||
    canAccessCourseTimeslotsSection;
  const canAccessCourseRootSection =
    canViewCourses ||
    canCreateCourses ||
    canUpdateCourses ||
    canDeleteCourses;
  const canAccessCourseMenu =
    canAccessCourseRootSection ||
    canAccessCourseConfigurationsSection ||
    canViewCourseReports;

  const name = process.env.REACT_APP_ADMIN_PREFIX;
  const adminRoleDisplay = (() => {
    try {
      const localRole = String(localStorage.getItem("role") || "")
        .toLowerCase()
        .trim();
      const rawUserData = localStorage.getItem("userData");
      const userData = rawUserData ? JSON.parse(rawUserData) : null;
      const userPortalRole = String(
        userData?.portal_role || userData?.role || ""
      )
        .toLowerCase()
        .trim();
      const role = userPortalRole || localRole;
      return role === "super admin" || role === "super_admin"
        ? "Super Admin"
        : "System Administrator";
    } catch {
      return "System Administrator";
    }
  })();

  // Admin logout function
  const handleLogout = () => {
    // Remove admin-role from localStorage
    localStorage.removeItem('role');
    localStorage.removeItem('token'); // Also remove token if exists
    localStorage.removeItem('admin-permissions');
    window.location.href = "/login";
  };

  const handleToggle = (label) => {
    setOpenMenus((prev) => {
      const newOpenMenus = { ...prev };
      newOpenMenus[label] = !prev[label];
      return newOpenMenus;
    });
  };

  // Build a unique key for nested menu (e.g. "Course|Course Configurations")
  const getMenuKey = (parentKey, label) => (parentKey ? `${parentKey}|${label}` : label);

  // Check if any descendant has the given path
  const hasDescendantWithPath = (items, path) => {
    if (!items || !items.length) return false;
    return items.some((it) => it.path === path || (it.children && hasDescendantWithPath(it.children, path)));
  };

  // Find all menu keys that lead to the given path (for auto-expand)
  const findOpenKeysForPath = (items, path, parentKey = null) => {
    for (const item of items) {
      if (item.path && item.path === path) {
        if (!parentKey) return [];
        return parentKey.split("|").reduce((acc, _, i, parts) => {
          acc.push(parts.slice(0, i + 1).join("|"));
          return acc;
        }, []);
      }
      if (item.children) {
        const key = getMenuKey(parentKey, item.label);
        const nested = findOpenKeysForPath(item.children, path, key);
        if (nested.length > 0) return nested;
      }
    }
    return [];
  };

  // Helper function to check if menu item should be highlighted
  const isMenuActive = (item) => {
    const currentPath = location.pathname;

    // Handle items with children
    if (item.children) {
      // Check if any child or nested descendant path matches
      if (hasDescendantWithPath(item.children, currentPath)) return true;
      const hasActiveChild = item.children.some(child => currentPath === child.path);
      if (hasActiveChild) return true;

      // Special handling for Students - active for all student-related URLs
      if (item.label === "Students" && currentPath.includes("/student")) {
        return true;
      }

      // Course: list, edit, assignments, content, topics, timeslots, report (avoid matching /course-test)
      if (item.label === "Course") {
        if (currentPath === "/admin/course/billing") return true;
        if (
          currentPath.includes("/course-list") ||
          currentPath.includes("/course-assignment") ||
          currentPath.includes("/assignment-list") ||
          currentPath.includes("/course-content") ||
          currentPath.includes("/topic/subtopic-list") ||
          currentPath.includes("/timeslot") ||
          currentPath.includes("/course-report") ||
          /\/admin\/course\/?(\d+)?$/.test(currentPath)
        ) {
          return true;
        }
      }

      // Special handling for Mock Exams - active for mock-exams/billing
      if (item.label === "Mock Exams" && currentPath === "/admin/mock-exams/billing") {
        return true;
      }

      // Special handling for Papers - active for papers, papers/billing, and paper-extract
      if (item.label === "Papers" && (currentPath === "/admin/papers" || currentPath === "/admin/papers/billing" || currentPath === "/admin/mock-exams/paper-extract")) {
        return true;
      }

      // Special handling for Question Bank - active for all question-bank routes (excluding flagged-questions which is its own menu)
      if (item.label === "Question Bank" && currentPath.startsWith("/admin/question-bank") && !currentPath.startsWith("/admin/flagged-questions")) {
        return true;
      }
    }

    // Handle leaf items
    if (item.path) {
      if (currentPath === item.path) return true;

      // Special handling for Billing - active for main billing list and specific parent billing
      if (item.label === "Billing" && (currentPath === "/admin/billing" || currentPath.includes("/billing"))) {
        return true;
      }

      // Billing & Transactions + per-parent billing (permission: view-parents-subscriptions)
      if (item.label === "Billing & Transactions" && (
        currentPath === "/admin/billing-transactions" ||
        currentPath.match(/^\/admin\/parent\/\d+\/billing(?:\/(course|mock))?$/)
      )) {
        return true;
      }

      if (item.label === "Mobile Users" && currentPath.startsWith("/admin/mobile-users")) {
        return true;
      }

      // Special handling for Parents - active for parent list AND individual parent billing pages
      if (item.label === "Parents" && (
        currentPath.startsWith("/admin/parents") ||
        currentPath.match(/^\/admin\/parent\/\d+\/billing(?:\/(course|mock))?$/)
      )) {
        return true;
      }

      // Special handling for Announcements
      if (item.label === "Announcements" && currentPath.startsWith("/admin/announcements")) {
        return true;
      }
      // Special handling for Awards
      if (item.label === "Awards" && currentPath.startsWith("/admin/awards")) {
        return true;
      }
      // Special handling for Certificates
      if (item.label === "Certificates" && currentPath.startsWith("/admin/certificates")) {
        return true;
      }

      // Special handling for Flash Cards
      if (item.label === "Flash Cards" && currentPath.startsWith("/admin/flash-cards")) {
        return true;
      }

      // Games: game topics + game contents admin routes
      if (
        item.label === "Games" &&
        (currentPath.startsWith("/admin/game-topics") ||
          currentPath.startsWith("/admin/game-contents"))
      ) {
        return true;
      }

      // Assignments: list, add/edit test, AI creator (path prefix may be /admin or REACT_APP_ADMIN_PREFIX)
      if (
        item.label === "Assignments" &&
        (currentPath.includes("/test-list") ||
          currentPath.includes("/course-test") ||
          currentPath.startsWith("/admin/ai-assignment-creator"))
      ) {
        return true;
      }

      if (
        item.label === "Analytics" &&
        currentPath.startsWith("/admin/analytics")
      ) {
        return true;
      }

      // Special handling for Leads section (list, tuition, mobile requests, WhatsApp)
      if (
        item.label === "Leads" &&
        (currentPath.startsWith("/admin/leads") ||
          currentPath.startsWith("/admin/tuition-requests") ||
          currentPath.startsWith("/admin/mobile-revision-support-requests") ||
          currentPath.startsWith("/admin/mobile-help-support") ||
          currentPath.startsWith("/admin/mobile-app-issues") ||
          currentPath.startsWith("/admin/subscriptions") ||
          currentPath.startsWith("/admin/whatsapp"))
      ) {
        return true;
      }
    }

    return false;
  };

  const fetchUnreadCount = async () => {
    if (!canViewAnnouncements) {
      setAnnouncementCount(0);
      return;
    }
    try {
      const response = await api.get("admin/notifications/unread-announcement-count");
      if (response.data?.success && response.data?.data?.count != null) {
        setAnnouncementCount(Number(response.data.data.count) || 0);
      } else {
        setAnnouncementCount(0);
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
      setAnnouncementCount(0);
    }
  };

  // When user opens Announcements page, mark all announcement notifications as read so badge shows 0
  useEffect(() => {
    if (!canViewAnnouncements) {
      setAnnouncementCount(0);
      return;
    }
    if (location.pathname.startsWith("/admin/announcements")) {
      api.post("admin/notifications/mark-all-announcements-read").then(() => {
        setAnnouncementCount(0);
      }).catch(() => {});
    } else {
      fetchUnreadCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, canViewAnnouncements]);

  const menuItems = [
    { label: "Dashboard", path: "/admin/dashboard", icon: <DashboardIcon /> },
    { label: "Resource Allocation", path: "/admin/resource-allocation", icon: <AssignmentTurnedInIcon /> },
    { label: "Holiday Management", path: "/admin/holiday-management", icon: <BeachAccessIcon /> },
    ...(canAccessCourseMenu
      ? [
          {
            label: "Course",
            icon: <SchoolIcon />,
            children: [
              ...(canViewCourses
                ? [
                    {
                      label: "Course List",
                      path: `/${name}/course-list`,
                      icon: <ListIcon />,
                    },
                  ]
                : []),
              ...(canCreateCourses
                ? [{ label: "Add Course", path: `/${name}/course`, icon: <AddIcon /> }]
                : []),
              ...(canAccessCourseConfigurationsSection
                ? [
                    {
                      label: "Course Configurations",
                      icon: <SchoolIcon />,
                      children: [
                        ...(canAccessCourseContentSection &&
                        (canViewCourseTopics || canCreateCourseTopics)
                          ? [
                              {
                                label: "Course Content",
                                icon: <TopicIcon />,
                                children: [
                                  ...(canViewCourseTopics
                                    ? [
                                        {
                                          label: "Content List",
                                          path: `/${name}/topic/subtopic-list`,
                                          icon: <ListIcon />,
                                        },
                                      ]
                                    : []),
                                  ...(canCreateCourseTopics
                                    ? [
                                        {
                                          label: "Add Content",
                                          path: `/${name}/course-content`,
                                          icon: <VideoLibraryIcon />,
                                        },
                                      ]
                                    : []),
                                ],
                              },
                            ]
                          : []),
                        ...(canAccessCourseDurationSection &&
                        (canViewCourseDurations ||
                          canViewAssignments ||
                          canCreateAssignments ||
                          canUpdateAssignments ||
                          canDeleteAssignments ||
                          canCreateCourseDurations)
                          ? [
                              {
                                label: "Course Duration",
                                icon: <AssignmentIcon />,
                                children: [
                                  ...(canViewCourseDurations ||
                                  canViewAssignments ||
                                  canCreateAssignments ||
                                  canUpdateAssignments ||
                                  canDeleteAssignments
                                    ? [
                                        {
                                          label: "Duration List",
                                          path: `/${name}/assignment-list`,
                                          icon: <ListIcon />,
                                        },
                                      ]
                                    : []),
                                  ...(canCreateCourseDurations || canCreateAssignments
                                    ? [
                                        {
                                          label: "Add Duration",
                                          path: `/${name}/course-assignment`,
                                          icon: <AddIcon />,
                                        },
                                      ]
                                    : []),
                                ],
                              },
                            ]
                          : []),
                          ...(canAccessCourseTimeslotsSection
                            ? [
                                {
                                  label: "Timeslots",
                                  path: "/admin/timeslot",
                                  icon: <AccessTimeIcon />,
                                },
                              ]
                            : []),
                      ],
                    },
                  ]
                : []),
              ...(canViewCourseReports
                ? [
                    {
                      label: "Course Report",
                      path: `/${name}/course-report`,
                      icon: <BarChartIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canAccessMockExamsMenu
      ? [
          {
            label: "Mock Exams",
            icon: <QuizIcon />,
            children: [
              {
                label: "Mock Exam List",
                path: "/admin/mock-exams",
                icon: <ListIcon />,
              },
            ],
          },
        ]
      : []),
    ...(canAccessPapersMenu
      ? [
          {
            label: "Papers",
            icon: <DescriptionIcon />,
            children: [
              ...(canViewPapers ||
              canCreatePapers ||
              canUpdatePapers ||
              canDeletePapers
                ? [
                    {
                      label: "Paper List",
                      path: "/admin/papers",
                      icon: <ListIcon />,
                    },
                  ]
                : []),
              ...(canAccessPaperExtract
                ? [
                    {
                      label: "Extract Paper",
                      path: "/admin/mock-exams/paper-extract",
                      icon: <FileDownloadIcon />,
                    },
                  ]
                : []),
              ...(canViewPaperPurchases
                ? [
                    {
                      label: "Paper purchases",
                      path: "/admin/papers/billing",
                      icon: <ReceiptLongIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canAccessAssignmentsMenu
      ? [
          {
            label: "Assignments",
            icon: <QuizIcon />,
            children: [
              ...(canViewAssignments ||
              canCreateAssignments ||
              canUpdateAssignments ||
              canDeleteAssignments
                ? [
                    {
                      label: "Assignment List",
                      path: `/${name}/test-list`,
                      icon: <ListIcon />,
                    },
                  ]
                : []),
              ...(canCreateAssignments || canUpdateAssignments
                ? [
                    {
                      label: "Add Assignment",
                      path: `/${name}/course-test`,
                      icon: <AddIcon />,
                    },
                  ]
                : []),
              ...(canUseAiAssignmentCreator ||
              canCreateAssignments ||
              canUpdateAssignments
                ? [
                    {
                      label: "AI Assignment Creator",
                      path: "/admin/ai-assignment-creator",
                      icon: <AutoAwesomeIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canViewFlashCards || canCreateFlashCards
      ? [
          {
            label: "Flash Cards",
            icon: <StyleIcon />,
            children: [
              ...(canViewFlashCards
                ? [
                    {
                      label: "Flash Card List",
                      path: "/admin/flash-cards",
                      icon: <ListIcon />,
                    },
                  ]
                : []),
              ...(canCreateFlashCards
                ? [
                    {
                      label: "Add Flash Card",
                      path: "/admin/flash-cards/add",
                      icon: <AddIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canAccessGamesMenu
      ? [
          {
            label: "Games",
            icon: <SportsEsportsIcon />,
            children: [
              ...(canAccessGameTopicsSection
                ? [
                    {
                      label: "Game Topics",
                      icon: <TopicIcon />,
                      children: [
                        ...(canViewGameTopics
                          ? [
                              {
                                label: "Game Topics List",
                                path: "/admin/game-topics",
                                icon: <ListIcon />,
                              },
                            ]
                          : []),
                        ...(canCreateGameTopics
                          ? [
                              {
                                label: "Add Game Topic",
                                path: "/admin/game-topics/add",
                                icon: <AddIcon />,
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              ...(canAccessGameContentsSection
                ? [
                    {
                      label: "Game Content",
                      icon: <DescriptionIcon />,
                      children: [
                        ...(canViewGameContents
                          ? [
                              {
                                label: "Game Contents List",
                                path: "/admin/game-contents",
                                icon: <ListIcon />,
                              },
                            ]
                          : []),
                        ...(canCreateGameContents
                          ? [
                              {
                                label: "Add Game Content",
                                path: "/admin/game-contents/add",
                                icon: <AddIcon />,
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canAccessQuestionBankMenu
      ? [
          {
            label: "Question Bank",
            icon: <LibraryBooksIcon />,
            children: [
              ...(canViewQuestionBank
                ? [
                    {
                      label: "All Questions",
                      path: "/admin/question-bank",
                      icon: <ListIcon />,
                    },
                  ]
                : []),
              ...(canCreateQuestionBank
                ? [
                    {
                      label: "Add Question",
                      path: "/admin/question-bank/create",
                      icon: <AddIcon />,
                    },
                  ]
                : []),
              ...(canViewQuestionBankImports || canCreateQuestionBankImports
                ? [
                    {
                      label: "Import from PDF",
                      path: "/admin/question-bank/import",
                      icon: <UploadFileIcon />,
                    },
                  ]
                : []),
              ...(canViewQuestionBankImports
                ? [
                    {
                      label: "Recent Imports",
                      path: "/admin/question-bank/recent-imports",
                      icon: <HistoryIcon />,
                    },
                  ]
                : []),
              ...(canViewQuestionBankPapers
                ? [
                    {
                      label: "Papers (PDF)",
                      path: "/admin/question-bank/papers",
                      icon: <DescriptionIcon />,
                    },
                  ]
                : []),
              ...(canViewQuestionBankSimilarPapers ||
              canUpdateQuestionBankSimilarPapers
                ? [
                    {
                      label: "Similar Papers",
                      path: "/admin/question-bank/similar-papers",
                      icon: <ContentCopyIcon />,
                    },
                  ]
                : []),
              ...(canViewQuestionBankImports
                ? [
                    {
                      label: "MCQ Conversions",
                      path: "/admin/question-bank/mcq-review",
                      icon: <AutoAwesomeIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canViewFlaggedQuestions
      ? [{ label: "Flagged Questions", path: "/admin/flagged-questions", icon: <FlagIcon /> }]
      : []),
    ...(canAccessMockExamCategories || canAccessPaperCategories
      ? [
          {
            label: "Exam categories",
            icon: <CategoryIcon />,
            children: [
              ...(canAccessMockExamCategories
                ? [
                    {
                      label: "Mock exam categories",
                      path: "/admin/mock-exam-categories",
                      icon: <CategoryIcon />,
                    },
                  ]
                : []),
              ...(canAccessPaperCategories
                ? [
                    {
                      label: "Paper categories",
                      path: "/admin/paper-categories",
                      icon: <CategoryIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canViewStudents || canCreateStudents
      ? [
          {
            label: "Students",
            icon: <PeopleIcon />,
            children: [
              ...(canViewStudents
                ? [
                    {
                      label: "Student List",
                      path: "/admin/students",
                      icon: <ListIcon />,
                    },
                  ]
                : []),
              ...(canCreateStudents
                ? [
                    {
                      label: "Add Student",
                      path: "/admin/student/add",
                      icon: <PersonAddIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canViewTutors
      ? [
          {
            label: "Tutors",
            icon: <SchoolIcon />,
            children: [
              ...(canViewTutors
                ? [
                    {
                      label: "Tutor List",
                      path: "/admin/tutors",
                      icon: <ListIcon />,
                    },
                  ]
                : []),
              ...(canUpdateTutors
                ? [
                    {
                      label: "Add Tutor",
                      path: "/admin/tutor/add",
                      icon: <PersonAddIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // {
    //   label: "Papers",
    //   icon: <DescriptionIcon />,
    //   children: [
    //     { label: "Paper Categories", path: "/admin/paper-categories", icon: <CategoryIcon /> },
    //     { label: "Papers", path: "/admin/papers", icon: <DescriptionIcon /> },
    //   ],
    // },
    ...(canViewParents ? [{ label: "Parents", path: "/admin/parents", icon: <PeopleIcon /> }] : []),
    ...(canViewParentsSubscriptions
      ? [
          {
            label: "Billing & Transactions",
            icon: <SchoolIcon />,
            path: "/admin/billing-transactions",
          },
        ]
      : []),
    ...(canViewMobileUsers
      ? [
          {
            label: "Mobile Users",
            icon: <PhoneIphoneIcon />,
            path: "/admin/mobile-users",
          },
        ]
      : []),
    ...(canAccessLeadsSection
      ? [
          {
            label: "Leads",
            icon: <ChatIcon />,
            children: [
              ...(canViewMobileHelpSupport
                ? [
                    {
                      label: "Mobile Help & Support",
                      path: "/admin/mobile-help-support",
                      icon: <ContactSupportIcon />,
                    },
                    {
                      label: "Mobile App Issues",
                      path: "/admin/mobile-app-issues",
                      icon: <ReportProblemIcon />,
                    },
                  ]
                : []),
              ...(canViewLeads
                ? [
                    {
                      label: "Lead List",
                      path: "/admin/leads",
                      icon: <PeopleIcon />,
                    },
                    {
                      label: "Tuition Requests",
                      path: "/admin/tuition-requests",
                      icon: <AssignmentIcon />,
                    },
                    {
                      label: "Mobile revision support",
                      path: "/admin/mobile-revision-support-requests",
                      icon: <AssignmentIcon />,
                    },
                    {
                      label: "Subscriptions",
                      path: "/admin/subscriptions",
                      icon: <PeopleIcon />,
                    },
                  ]
                : []),
              ...(canViewSupportChat
                ? [
                    {
                      label: "Support Chats",
                      path: "/admin/support-chat",
                      icon: <ChatIcon />,
                    },
                  ]
                : []),
              ...(canSendWhatsApp
                ? [
                    {
                      label: "WhatsApp Campaign",
                      path: "/admin/whatsapp",
                      icon: <ChatIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),

    // { label: "Billing", path: "/admin/billing", icon: <ReceiptLongIcon /> },
    ...(canViewAnnouncements
      ? [{ label: "Announcements", path: "/admin/announcements", icon: <NotificationsIcon /> }]
      : []),
    ...(canViewAwards
      ? [{ label: "Awards", path: "/admin/awards", icon: <EmojiEventsIcon /> }]
      : []),
    ...(canViewCertificates
      ? [{ label: "Certificates", path: "/admin/certificates", icon: <PictureAsPdfIcon /> }]
      : []),
    ...(canManageExamModes
      ? [{ label: "Exam Modes", path: "/admin/exam-modes", icon: <ScienceIcon /> }]
      : []),
    ...(canManageExamModes
      ? [{ label: "Settings", path: "/admin/settings", icon: <SettingsIcon /> }]
      : []),
    ...(canViewAnalytics
      ? [{ label: "Analytics", path: "/admin/analytics", icon: <InsightsIcon /> }]
      : []),
    ...(canAccessMasterForms
      ? [{ label: "Master Forms", path: "/admin/master-forms", icon: <StorageIcon /> }]
      : []),
    ...(canViewVerificationDashboard
      ? [{ label: "Verification Dashboard", path: "/admin/verification-dashboard", icon: <VerifiedUserIcon /> }]
      : []),
    ...(canViewAuditTrail
      ? [{ label: "Audit Trail", path: "/admin/audit-trail", icon: <HistoryIcon /> }]
      : []),
  ];

  // Simulate page load finished by hiding loader after mount
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Auto-expand parent menu when child route is active and close others
  useEffect(() => {
    const currentPath = location.pathname;
    const newOpenMenus = {};

    menuItems.forEach((menuItem) => {
      if (menuItem.children) {
        const keysToOpen = findOpenKeysForPath([menuItem], currentPath, null);
        keysToOpen.forEach((k) => { newOpenMenus[k] = true; });
      }
    });

    setOpenMenus(newOpenMenus);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      {/* Show loader only when loading is true */}
      {loading && <LinearProgress sx={{ height: 4 }} />}

      {/* Container for AppBar and main layout */}
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden", minHeight: 0 }}>
        {/* Fancy Top App Bar */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
            boxShadow: "none",
            top: loading ? "4px" : 0,
            transition: "top 0.3s",
          }}
        >
          <Toolbar sx={{
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 3 },
            minHeight: { xs: 56, sm: 70 },
            height: { xs: 56, sm: 70 },
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(10px)",
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}>
              {/* Sidebar Toggle Button */}
              <Tooltip title={sidebarOpen ? "Hide Sidebar" : "Show Sidebar"} arrow>
                <IconButton
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  sx={{
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    borderRadius: "12px",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.25)",
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.3)"
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  <MenuIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  p: { xs: 1, sm: 1.5 },
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
                }}
              >
                <Typography sx={{ fontSize: '28px' }}>🔧</Typography>
              </Box>
              <Box sx={{ minWidth: 0, maxWidth: { xs: '42vw', sm: 'none' } }}>
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.375rem' },
                    textShadow: "0 2px 4px rgba(0,0,0,0.3)"
                  }}
                >
                  {CapitalizeFirstLetter(name)} Panel
                </Typography>
              </Box>
            </Box>

            <TesterPortalSwitcher />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flexShrink: 0 }}>
              {/* Admin Profile Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%",
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AccountCircle sx={{ color: "#fff", fontSize: 28 }} />
                </Box>
                <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: '14px'
                    }}
                  >
                    Admin Portal
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: '11px'
                    }}
                  >
                    {adminRoleDisplay}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  "&:hover": { background: "rgba(255,255,255,0.25)" },
                }}
              >
                <Logout />
              </IconButton>
              <Button
                onClick={handleLogout}
                variant="contained"
                size="medium"
                startIcon={<Logout />}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  fontSize: '13px',
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.25)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Logout
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Fancy Sidebar */}
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: isMobile ? 0 : (sidebarOpen ? drawerWidth : 0),
            flexShrink: 0,
            alignSelf: "stretch",
            minHeight: 0,
            transition: 'width 0.3s ease',
            [`& .MuiDrawer-paper`]: {
              width: sidebarOpen ? drawerWidth : 0,
              boxSizing: "border-box",
              mt: `${appBarHeight}px`,
              maxHeight: `calc(100vh - ${appBarHeight}px)`,
              background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
              borderRight: "1px solid #dee2e6",
              boxShadow: sidebarOpen ? "2px 0 10px rgba(0,0,0,0.1)" : "none",
              overflowY: sidebarOpen ? "auto" : "hidden",
              overflowX: "hidden",
              transition: "all 0.3s ease",
              // Custom scrollbar styling
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(0,0,0,0.05)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.2)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
              },
              // Firefox scrollbar
              scrollbarWidth: "thin",
              scrollbarColor: "#667eea rgba(0,0,0,0.05)",
            },
          }}
        >
          <List sx={{ pt: 2, pb: 3 }}>
            {menuItems.map((item) => (
              <Box key={item.label} sx={{ mb: 1 }}>
                {item.children ? (
                  <>
                    <ListItemButton
                      onClick={() => handleToggle(item.label)}
                      sx={{
                        mx: 1,
                        borderRadius: 2,
                        mb: 0.5,
                        background: (openMenus[item.label] || isMenuActive(item))
                          ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                          : "transparent",
                        color: (openMenus[item.label] || isMenuActive(item)) ? "#fff" : "#111827",
                        "&:hover": {
                          background: (openMenus[item.label] || isMenuActive(item))
                            ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                            : "rgba(102, 126, 234, 0.1)",
                          transform: "translateX(4px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <ListItemIcon sx={{
                        color: (openMenus[item.label] || isMenuActive(item)) ? "#fff" : "#667eea",
                        minWidth: 40
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          "& .MuiTypography-root": {
                            fontWeight: 700,
                            fontSize: "14px"
                          }
                        }}
                      />
                      {(openMenus[item.label] || isMenuActive(item)) ?
                        <ExpandLess sx={{ color: "#fff" }} /> :
                        <ExpandMore sx={{ color: "#667eea" }} />
                      }
                    </ListItemButton>
                    <Collapse in={openMenus[item.label]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => (
                          (() => {
                            const menuKey = getMenuKey(item.label, child.label);
                            if (child.children) {
                              return (
                                <Box key={child.label}>
                                  <ListItemButton
                                    onClick={() => handleToggle(menuKey)}
                                    sx={{
                                      pl: 5,
                                      mx: 1,
                                      borderRadius: 2,
                                      mb: 0.5,
                                      background: (openMenus[menuKey] || hasDescendantWithPath(child.children, location.pathname))
                                        ? "linear-gradient(90deg, #5a67d8 0%, #6b46c1 100%)"
                                        : "transparent",
                                      color: (openMenus[menuKey] || hasDescendantWithPath(child.children, location.pathname)) ? "#fff" : "#1f2937",
                                      "&:hover": {
                                        background: (openMenus[menuKey] || hasDescendantWithPath(child.children, location.pathname))
                                          ? "linear-gradient(90deg, #5a67d8 0%, #6b46c1 100%)"
                                          : "rgba(59, 42, 159, 0.08)",
                                        transform: "translateX(6px)",
                                      },
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    <ListItemIcon sx={{ color: (openMenus[menuKey]) ? "#fff" : "#5a67d8", minWidth: 32 }}>
                                      {child.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={child.label}
                                      sx={{ "& .MuiTypography-root": { fontSize: "13px", fontWeight: 700 } }}
                                    />
                                    {(openMenus[menuKey]) ? <ExpandLess sx={{ fontSize: 18, color: "#fff" }} /> : <ExpandMore sx={{ fontSize: 18, color: "#5a67d8" }} />}
                                  </ListItemButton>
                                  <Collapse in={openMenus[menuKey]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                      {child.children.map((subChild) => (
                                        (() => {
                                          const subKey = getMenuKey(menuKey, subChild.label);
                                          if (subChild.children) {
                                            return (
                                              <Box key={subChild.label}>
                                                <ListItemButton
                                                  onClick={() => handleToggle(subKey)}
                                                  sx={{
                                                    pl: 7,
                                                    mx: 1,
                                                    borderRadius: 2,
                                                    mb: 0.5,
                                                    background: openMenus[subKey] ? "rgba(59, 42, 159, 0.2)" : "transparent",
                                                    color: "#1f2937",
                                                    "&:hover": { background: "rgba(59, 42, 159, 0.12)", transform: "translateX(6px)" },
                                                    transition: "all 0.3s ease",
                                                  }}
                                                >
                                                  <ListItemIcon sx={{ color: "#3B2A9F", minWidth: 28 }}>{subChild.icon}</ListItemIcon>
                                                  <ListItemText primary={subChild.label} sx={{ "& .MuiTypography-root": { fontSize: "12px", color: "#1f2937", fontWeight: 700 } }} />
                                                  {openMenus[subKey] ? <ExpandLess sx={{ fontSize: 16, color: "#1f2937" }} /> : <ExpandMore sx={{ fontSize: 16, color: "#3B2A9F" }} />}
                                                </ListItemButton>
                                                <Collapse in={openMenus[subKey]} timeout="auto" unmountOnExit>
                                                  <List component="div" disablePadding>
                                                    {subChild.children.map((leaf) => (
                                                      <ListItemButton
                                                        key={leaf.label}
                                                        sx={{
                                                          pl: 9,
                                                          mx: 1,
                                                          borderRadius: 2,
                                                          mb: 0.5,
                                                          background: location.pathname === leaf.path ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)" : "transparent",
                                                          color: location.pathname === leaf.path ? "#fff" : "#1f2937",
                                                          "&:hover": {
                                                            background: location.pathname === leaf.path ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)" : "rgba(59, 42, 159, 0.1)",
                                                            transform: "translateX(8px)",
                                                          },
                                                          transition: "all 0.3s ease",
                                                        }}
                                                        onClick={() => leaf.path && navigate(leaf.path)}
                                                      >
                                                        <ListItemIcon sx={{ color: location.pathname === leaf.path ? "#fff" : "#3B2A9F", minWidth: 28 }}>{leaf.icon}</ListItemIcon>
                                                        <ListItemText primary={leaf.label} sx={{ "& .MuiTypography-root": { fontSize: "12px" } }} />
                                                      </ListItemButton>
                                                    ))}
                                                  </List>
                                                </Collapse>
                                              </Box>
                                            );
                                          }
                                          return (
                                            <ListItemButton
                                              key={subChild.label}
                                              sx={{
                                                pl: 7,
                                                mx: 1,
                                                borderRadius: 2,
                                                mb: 0.5,
                                                background: location.pathname === subChild.path ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)" : "transparent",
                                                color: location.pathname === subChild.path ? "#fff" : "#1f2937",
                                                "&:hover": {
                                                  background: location.pathname === subChild.path ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)" : "rgba(59, 42, 159, 0.1)",
                                                  transform: "translateX(8px)",
                                                },
                                                transition: "all 0.3s ease",
                                              }}
                                              onClick={() => subChild.path && navigate(subChild.path)}
                                            >
                                              <ListItemIcon sx={{ color: location.pathname === subChild.path ? "#fff" : "#3B2A9F", minWidth: 32 }}>{subChild.icon}</ListItemIcon>
                                              <ListItemText primary={subChild.label} sx={{ "& .MuiTypography-root": { fontSize: "13px", fontWeight: 700 } }} />
                                            </ListItemButton>
                                          );
                                        })()
                                      ))}
                                    </List>
                                  </Collapse>
                                </Box>
                              );
                            }
                            return (
                              <ListItemButton
                                key={child.label}
                                sx={{
                                  pl: 6,
                                  mx: 1,
                                  borderRadius: 2,
                                  mb: 0.5,
                                  background: location.pathname === child.path
                                    ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)"
                                    : "transparent",
                                  color: location.pathname === child.path ? "#fff" : "#1f2937",
                                  "&:hover": {
                                    background: location.pathname === child.path
                                      ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)"
                                      : "rgba(59, 42, 159, 0.1)",
                                    transform: "translateX(8px)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                                onClick={() => child.path && navigate(child.path)}
                              >
                                <ListItemIcon sx={{
                                  color: location.pathname === child.path ? "#fff" : "#3B2A9F",
                                  minWidth: 35
                                }}>
                                  {child.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.label}
                                  sx={{
                                    "& .MuiTypography-root": {
                                      fontWeight: 700,
                                      fontSize: "13px"
                                    }
                                  }}
                                />
                              </ListItemButton>
                            );
                          })()
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  <ListItemButton
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      mb: 0.5,
                      background: isMenuActive(item)
                        ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                        : "transparent",
                      color: isMenuActive(item) ? "#fff" : "#111827",
                      "&:hover": {
                        background: isMenuActive(item)
                          ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                          : "rgba(102, 126, 234, 0.1)",
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <ListItemIcon sx={{
                      color: isMenuActive(item) ? "#fff" : "#667eea",
                      minWidth: 40
                    }}>
                      {item.label === "Announcements" ? (
                        <Badge badgeContent={announcementCount} color="error" max={99} invisible={announcementCount === 0 || location.pathname.startsWith("/admin/announcements")}>
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: 700,
                          fontSize: "14px"
                        }
                      }}
                    />
                  </ListItemButton>
                )}
              </Box>
            ))}
          </List>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: 0,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            p: { xs: 1.5, sm: 2, md: 3 },
            pb: 8, // Extra bottom padding to prevent content cropping
            mt: `${appBarHeight}px`,
            overflowY: "auto",
            // Custom scrollbar styling for main content
            "&::-webkit-scrollbar": {
              width: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0,0,0,0.05)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              border: "2px solid #f9f9f9",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
            },
            // Firefox scrollbar
            scrollbarWidth: "thin",
            scrollbarColor: "#667eea rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ pb: 4 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
